import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { CalendarClock, Copy } from 'lucide-react';
import { adminApi, extractErrorMessage } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { DayOfWeek } from '@kardume/types';
import { BUSINESS_HOUR_ERROR_MESSAGES } from '@kardume/types';
import {
  PageHeader,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Button,
  Select,
  BusinessHoursInput,
  WEEK_DAYS,
  weekHoursFrom,
  weekHoursToPayload,
  validateWeekHours,
  hasWeekHoursErrors,
} from '@kardume/ui';
import type { DayHours, WeekHours } from '@kardume/ui';
import DataState from '../components/DataState';
import { useAsync } from '../utils/useAsync';

const Intro = styled.p`
  margin: 0 0 1rem;
  font-size: 0.85rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

/**
 * Atalho de repetição. Seis dias iguais digitados um a um é o que faz o lojista
 * desistir da tela na metade e deixar o horário errado no ar.
 */
const CopyBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${(p) => p.theme.border};

  .grow {
    min-width: 190px;
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1.25rem;
`;

const FooterHint = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: ${(p) => p.theme.textMuted};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

/**
 * Horário de atendimento da loja (card 295).
 *
 * Tela própria, e não uma aba do perfil (card 294), por três motivos que se
 * somam: a gravação é outra (`PUT /v1/merchant/business-hours` publica a semana
 * inteira, o perfil publica o cadastro), a alçada é outra
 * (`ROLE_MERCHANT_BUSINESS_HOURS_MANAGE`, que o gerente de turno tem sem poder
 * mexer no CNPJ) e a cadência é oposta - horário muda toda semana, o resto do
 * perfil quase nunca. Como aba, corrigir um feriado obrigaria a reenviar
 * imagens, comodidades e dados cadastrais junto.
 *
 * O turno que vira a noite é digitado como ele é - "das 18:30 à 01:00" - e quem
 * parte na meia-noite é o core. Esta tela não faz, e não pode fazer, essa conta.
 *
 * "Aberto agora" **não** é calculado aqui. Ele vem do core, que conhece o fuso
 * de negócio, as pausas e os fechamentos temporários; recalcular no cliente
 * daria uma segunda resposta para a mesma pergunta.
 */
const BusinessHoursPage: React.FC = () => {
  const { showNotification } = useNotification();

  const load = useCallback(() => adminApi.getBusinessHours(), []);
  const { data, loading, error, reload } = useAsync(load, []);

  const [week, setWeek] = useState<WeekHours | null>(null);
  /** Semana publicada, para saber o que ainda é rascunho. */
  const [published, setPublished] = useState<WeekHours | null>(null);
  const [copyFrom, setCopyFrom] = useState<DayOfWeek>('MONDAY');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!data) return;
    // O core manda sempre os sete dias, então `weekHoursFrom` nunca cai no
    // padrão de 09:00–18:00 aqui - o lojista só vê o que ele mesmo publicou.
    const loaded = weekHoursFrom(data.businessHours);
    setWeek(loaded);
    setPublished(loaded);
  }, [data]);

  const errors = useMemo(() => (week ? validateWeekHours(week) : null), [week]);
  const hasErrors = !!errors && hasWeekHoursErrors(errors);
  const dirty = !!week && !!published && JSON.stringify(week) !== JSON.stringify(published);

  const patchDay = (day: DayOfWeek, patch: Partial<DayHours>) =>
    setWeek((prev) => (prev ? { ...prev, [day]: { ...prev[day], ...patch } } : prev));

  /**
   * Repete um dia nos outros seis. Copia o dia inteiro - inclusive o "fechado" -
   * porque a loja que fecha às terças costuma fechar às terças, e não copiar o
   * estado deixaria seis dias meio preenchidos.
   */
  const copyToOtherDays = () => {
    setWeek((prev) => {
      if (!prev) return prev;
      const source = prev[copyFrom];
      const next = { ...prev };
      WEEK_DAYS.forEach(({ key }) => {
        if (key === copyFrom) return;
        next[key] = {
          closed: source.closed,
          windows: source.windows.map((window) => ({ ...window })),
        };
      });
      return next;
    });
    const label = WEEK_DAYS.find((d) => d.key === copyFrom)?.label ?? '';
    showNotification(`Horário de ${label} aplicado aos outros dias. Confira e salve.`, 'info');
  };

  const handleSave = async () => {
    if (!week || saving) return;
    if (hasErrors) {
      showNotification('Revise os horários destacados antes de publicar.', 'error');
      return;
    }
    setSaving(true);
    try {
      const updated = await adminApi.updateBusinessHours({ businessHours: weekHoursToPayload(week) });
      // Reler o que o core devolveu, e não manter o que foi digitado: é assim
      // que o lojista confere que o turno noturno voltou inteiro. Se algum dia
      // ele voltar partido, o erro aparece aqui, e não semanas depois na
      // página da loja.
      const saved = weekHoursFrom(updated.businessHours);
      setWeek(saved);
      setPublished(saved);
      showNotification('Horário de atendimento publicado.', 'success');
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível salvar os horários.', BUSINESS_HOUR_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    if (published) setWeek(published);
  };

  return (
    <>
      <PageHeader
        title="Horários de atendimento"
        subtitle="Quando a sua loja está aberta. É o que o cliente vê na sua página e o que decide o “aberto agora”."
        actions={dirty ? <Badge tone="warning">Alterações não salvas</Badge> : undefined}
      />

      <DataState loading={loading} error={error} onRetry={reload}>
        {week && (
          <Card>
            <CardHeader>
              <h3>
                <CalendarClock size={16} /> Semana de atendimento
              </h3>
            </CardHeader>
            <CardBody>
              <Intro>
                <strong>Salvar publica a semana inteira.</strong> O que estiver nesta tela passa a ser o
                horário da sua loja - não é uma linha a mais. Se a loja fecha para o almoço, use
                &quot;Adicionar horário&quot; e cadastre os dois turnos do dia. Para o turno que atravessa
                a madrugada, escreva como ele é: das <strong>18:30</strong> à <strong>01:00</strong>.
              </Intro>

              <CopyBar>
                <Select
                  className="grow"
                  label="Repetir o horário de"
                  value={copyFrom}
                  onChange={(e) => setCopyFrom(e.target.value as DayOfWeek)}
                  options={WEEK_DAYS.map((day) => ({ value: day.key, label: day.label }))}
                />
                <Button type="button" variant="outline" onClick={copyToOtherDays}>
                  <Copy size={15} /> Aplicar nos outros dias
                </Button>
              </CopyBar>

              <BusinessHoursInput value={week} onChange={patchDay} disabled={saving} />
            </CardBody>
          </Card>
        )}

        {week && (
          <Footer>
            <FooterHint>
              {hasErrors
                ? 'Há horários a corrigir - os campos com problema estão marcados acima.'
                : 'O “aberto agora” da sua página é calculado a partir daqui, no horário de Brasília.'}
            </FooterHint>
            <Actions>
              {dirty && (
                <Button type="button" variant="ghost" onClick={discard} disabled={saving}>
                  Descartar alterações
                </Button>
              )}
              <Button type="button" variant="primary" onClick={handleSave} isLoading={saving}>
                Salvar horários
              </Button>
            </Actions>
          </Footer>
        )}
      </DataState>
    </>
  );
};

export default BusinessHoursPage;
